// VencimientosFiscalesSection
// Lista de vencimientos DGI + BPS de Limpack SAS.
// Solo visible para superadmin (Roberto). Gate con isSuperadmin en el llamador.
// Variantes:
//   - mode="full"   → seccion completa con tabla + ICS subscribe (en /admin/finanzas/reportes)
//   - mode="card"   → card compacto con próximo vencimiento (en /admin Dashboard)

import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const fmt = (n) => '$' + Math.round(Number(n || 0)).toLocaleString('es-UY')

function fmtFecha(d) {
  if (!d) return '—'
  const [y, m, day] = d.split('-')
  return `${day}/${m}/${y}`
}

function diasHasta(d) {
  if (!d) return null
  const hoy = new Date()
  hoy.setHours(0, 0, 0, 0)
  const target = new Date(d + 'T00:00:00')
  const diff = Math.round((target - hoy) / (1000 * 60 * 60 * 24))
  return diff
}

function emojiOrg(org) {
  if (org === 'DGI') return '🏛️'
  if (org === 'BPS') return '👥'
  return '📅'
}

function tipoLabel(tipo, periodo) {
  switch (tipo) {
    case 'bps_mensual': return `BPS · ${periodo}`
    case 'iva_dgi': return `IVA DGI · ${periodo}`
    case 'irae_anticipo': return `Anticipo IRAE · ${periodo}`
    case 'irae_anual': return `IRAE anual · ${periodo}`
    case 'f2148': return `F2148 · ${periodo}`
    default: return `${tipo} · ${periodo}`
  }
}

// Devuelve paleta + intensidad según cuán cerca está el vencimiento.
// Usado en badge del header (bg/fg) y también en el card completo (cardBg/cardBorder/cardFg).
function colorPorDias(dias, estado) {
  if (estado === 'pagado') {
    return {
      bg: '#dcfce7', fg: '#166534', label: 'Pagado',
      cardBg: '#f0fdf4', cardBorder: '#bbf7d0', cardFg: '#166534', tier: 'pagado',
    }
  }
  if (dias < 0) {
    // VENCIDO — rojo fuerte
    return {
      bg: '#fee2e2', fg: '#991b1b', label: `Vencido hace ${-dias}d`,
      cardBg: '#fef2f2', cardBorder: '#fca5a5', cardFg: '#991b1b', tier: 'critico',
    }
  }
  if (dias === 0) {
    // HOY — rojo crítico
    return {
      bg: '#fecaca', fg: '#7f1d1d', label: 'Vence HOY',
      cardBg: '#fef2f2', cardBorder: '#f87171', cardFg: '#7f1d1d', tier: 'critico',
    }
  }
  if (dias <= 2) {
    // 1-2 días — naranja intenso
    return {
      bg: '#fed7aa', fg: '#9a3412', label: `En ${dias}d`,
      cardBg: '#fff7ed', cardBorder: '#fdba74', cardFg: '#9a3412', tier: 'urgente',
    }
  }
  if (dias <= 7) {
    // 3-7 días — amarillo
    return {
      bg: '#fef3c7', fg: '#92400e', label: `En ${dias}d`,
      cardBg: '#fffbeb', cardBorder: '#fde68a', cardFg: '#92400e', tier: 'pronto',
    }
  }
  if (dias <= 15) {
    // 8-15 días — azul tranquilo
    return {
      bg: '#dbeafe', fg: '#1e40af', label: `En ${dias}d`,
      cardBg: '#eff6ff', cardBorder: '#bfdbfe', cardFg: '#1e40af', tier: 'tranquilo',
    }
  }
  // 16+ días — gris neutro
  return {
    bg: '#f1f5f9', fg: '#475569', label: `En ${dias}d`,
    cardBg: '#ffffff', cardBorder: '#e2e8f0', cardFg: '#475569', tier: 'lejano',
  }
}

async function getICSUrl() {
  const { data } = await supabase.from('app_config').select('value').eq('key', 'VENCIMIENTOS_ICS_TOKEN').single()
  if (!data?.value) return null
  let token = data.value
  if (typeof token === 'string' && token.startsWith('"') && token.endsWith('"')) {
    try { token = JSON.parse(token) } catch {}
  }
  const base = import.meta.env.VITE_SUPABASE_URL || 'https://xsviveqhkppmhncodhuv.supabase.co'
  return `${base}/functions/v1/finanzas-cal-ics?t=${token}`
}

export default function VencimientosFiscalesSection({ mode = 'full' }) {
  const [vencs, setVencs] = useState([])
  const [loading, setLoading] = useState(true)
  const [icsUrl, setIcsUrl] = useState(null)
  const [copiado, setCopiado] = useState(false)
  const [modal, setModal] = useState(null) // { id, ... } para marcar pagado
  const [verTodos, setVerTodos] = useState(false)

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const hoyMenos30 = new Date()
    hoyMenos30.setDate(hoyMenos30.getDate() - 30)
    const { data } = await supabase
      .from('vencimientos_fiscales')
      .select('*')
      .gte('fecha_vencimiento', hoyMenos30.toISOString().slice(0, 10))
      .order('fecha_vencimiento')
      .limit(mode === 'card' ? 5 : 50)
    setVencs(data || [])
    if (mode === 'full') {
      setIcsUrl(await getICSUrl())
    }
    setLoading(false)
  }

  async function marcarPagado(form) {
    const { error } = await supabase
      .from('vencimientos_fiscales')
      .update({
        estado: 'pagado',
        fecha_pago: form.fecha_pago,
        monto_pagado: form.monto_pagado ? Number(form.monto_pagado) : null,
        notas: form.notas || null,
      })
      .eq('id', modal.id)
    if (error) {
      alert('Error: ' + error.message)
      return
    }
    setModal(null)
    load()
  }

  async function copiarUrl() {
    if (!icsUrl) return
    try {
      await navigator.clipboard.writeText(icsUrl)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2500)
    } catch (e) {
      alert('No pude copiar. Copialo manual:\n\n' + icsUrl)
    }
  }

  // ──────── MODE CARD (Dashboard compact) ────────
  if (mode === 'card') {
    const pendientes = vencs.filter(v => v.estado !== 'pagado')
    if (loading) return <div style={{ padding: 12, color: '#64748B' }}>Cargando vencimientos…</div>
    if (pendientes.length === 0) {
      return (
        <div style={{ padding: 16, background: '#dcfce7', borderRadius: 10, border: '1px solid #86efac' }}>
          <div style={{ fontWeight: 800, color: '#166534', fontSize: 14 }}>✓ Sin vencimientos próximos</div>
          <div style={{ fontSize: 12, color: '#15803d', marginTop: 2 }}>Todo al día con DGI y BPS.</div>
        </div>
      )
    }
    const proximo = pendientes[0]
    const dias = diasHasta(proximo.fecha_vencimiento)
    const c = colorPorDias(dias, proximo.estado)

    // Crítico (HOY o vencido) → pulso suave para llamar la atención
    const pulsa = c.tier === 'critico'

    return (
      <div style={{
        background: c.cardBg,
        borderRadius: 10,
        border: `1.5px solid ${c.cardBorder}`,
        overflow: 'hidden',
        transition: 'all .25s ease',
        boxShadow: c.tier === 'critico'
          ? '0 0 0 4px rgba(248, 113, 113, .15)'
          : c.tier === 'urgente'
            ? '0 0 0 3px rgba(253, 186, 116, .15)'
            : 'none',
        animation: pulsa ? 'venc-pulso 2.4s ease-in-out infinite' : 'none',
      }}>
        <div style={{
          padding: '10px 14px',
          background: c.bg,
          color: c.fg,
          fontWeight: 800,
          fontSize: 13,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${c.cardBorder}`,
        }}>
          <span>📅 Próximo vencimiento fiscal</span>
          <span>{c.label}</span>
        </div>
        <div style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 16 }}>{emojiOrg(proximo.organismo)}</span>
            <span style={{ fontWeight: 700, color: c.cardFg, fontSize: 14 }}>{tipoLabel(proximo.tipo, proximo.periodo)}</span>
          </div>
          <div style={{ fontSize: 12, color: c.cardFg, opacity: 0.85, marginBottom: 4 }}>
            Vence {fmtFecha(proximo.fecha_vencimiento)}
            {proximo.monto_estimado && ` · ${fmt(proximo.monto_estimado)} estimado`}
          </div>
          {pendientes.length > 1 && (
            <div style={{ fontSize: 11, color: c.cardFg, opacity: 0.7, marginTop: 6, paddingTop: 6, borderTop: `1px solid ${c.cardBorder}` }}>
              + {pendientes.length - 1} {pendientes.length - 1 === 1 ? 'vencimiento' : 'vencimientos'} más pendiente{pendientes.length - 1 === 1 ? '' : 's'}
            </div>
          )}
          <a href="/admin/finanzas/reportes#vencimientos" style={{ display: 'inline-block', marginTop: 8, fontSize: 12, color: c.cardFg, fontWeight: 800, textDecoration: 'underline', textUnderlineOffset: 2 }}>
            Ver todos →
          </a>
        </div>
        {pulsa && (
          <style>{`@keyframes venc-pulso {
            0%, 100% { box-shadow: 0 0 0 4px rgba(248, 113, 113, .15); }
            50%      { box-shadow: 0 0 0 8px rgba(248, 113, 113, .25); }
          }`}</style>
        )}
      </div>
    )
  }

  // ──────── MODE FULL ────────
  return (
    <section id="vencimientos" style={{ marginTop: 24 }}>
      <style>{`@keyframes venc-fila-pulso {
        0%, 100% { box-shadow: inset 4px 0 0 #f87171, inset -100vw 0 0 rgba(254, 226, 226, 0); }
        50%      { box-shadow: inset 4px 0 0 #dc2626, inset -100vw 0 0 rgba(254, 226, 226, 0.4); }
      }`}</style>
      <h2 className="cb-h2" style={{ marginBottom: 8, scrollMarginTop: 80 }}>📅 Vencimientos fiscales</h2>
      <p style={{ fontSize: 12, color: '#64748B', marginTop: 0, marginBottom: 12 }}>
        DGI (IVA + Anticipo IRAE) y BPS Limpack SAS. Suscribí el calendario abajo para recibir avisos en Google Calendar (2 días antes y el mismo día a las 9:00).
      </p>

      {loading ? (
        <div style={{ padding: 12, color: '#64748B' }}>Cargando…</div>
      ) : vencs.length === 0 ? (
        <div style={{ padding: 16, background: '#f8f9fb', borderRadius: 8, textAlign: 'center', color: '#64748B' }}>
          Sin vencimientos cargados.
        </div>
      ) : (() => {
        // Pendientes primero, ordenados por fecha. Pagados/viejos al final.
        const pendientes = vencs.filter(v => v.estado !== 'pagado')
        const pagados = vencs.filter(v => v.estado === 'pagado')
        const lista = [...pendientes, ...pagados]
        // Por defecto: 3 próximas (pendientes). Si Roberto expande, mostramos todas.
        const visibles = verTodos ? lista : pendientes.slice(0, 3)
        const ocultas = lista.length - visibles.length
        return (
          <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead style={{ background: '#f8f9fb' }}>
                  <tr>
                    <th style={th}>Fecha</th>
                    <th style={th}>Vence</th>
                    <th style={th}>Organismo</th>
                    <th style={th}>Concepto</th>
                    <th style={{ ...th, textAlign: 'right' }}>Monto est.</th>
                    <th style={th}>Estado</th>
                    <th style={{ ...th, textAlign: 'right' }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map(v => {
                    const dias = diasHasta(v.fecha_vencimiento)
                    const c = colorPorDias(dias, v.estado)
                    const esPagado = v.estado === 'pagado'
                    const esAutoCalc = v.tipo === 'iva_dgi' // hoy solo IVA tiene auto-cálculo
                    const pulsaFila = c.tier === 'critico' && !esPagado
                    return (
                      <tr key={v.id} style={{
                        borderTop: '1px solid #f1f5f9',
                        opacity: esPagado ? 0.55 : 1,
                        background: esPagado ? '#fafafa' : c.cardBg,
                        boxShadow: !esPagado ? `inset 4px 0 0 ${c.cardBorder}` : 'none',
                        animation: pulsaFila ? 'venc-fila-pulso 2.4s ease-in-out infinite' : 'none',
                      }}>
                        <td style={{ ...td, color: c.cardFg, fontWeight: 600 }}>{fmtFecha(v.fecha_vencimiento)}</td>
                        <td style={td}><span style={{ background: c.bg, color: c.fg, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>{c.label}</span></td>
                        <td style={{ ...td, color: c.cardFg }}>{emojiOrg(v.organismo)} <strong>{v.organismo}</strong></td>
                        <td style={td}>
                          <div style={{ color: c.cardFg, fontWeight: 700 }}>{tipoLabel(v.tipo, v.periodo)}</div>
                          {v.formulario && <div style={{ fontSize: 11, color: c.cardFg, opacity: 0.6 }}>Form {v.formulario}</div>}
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: c.cardFg, fontWeight: 700 }}>
                          {v.monto_estimado
                            ? <>
                                {fmt(v.monto_estimado)}
                                {esAutoCalc && <div style={{ fontSize: 10, color: '#0284c7', fontWeight: 600, marginTop: 2 }}>auto · facturas/compras</div>}
                              </>
                            : <span style={{ color: c.cardFg, opacity: 0.4 }}>—</span>}
                        </td>
                        <td style={td}>
                          {esPagado ? (
                            <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 12 }}>
                              ✓ Pagado {v.fecha_pago && fmtFecha(v.fecha_pago)}
                            </span>
                          ) : (
                            <span style={{ color: c.cardFg, opacity: 0.75, fontSize: 12, fontWeight: 600 }}>Pendiente</span>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          {!esPagado && (
                            <button onClick={() => setModal({ ...v })} style={btnSm}>Marcar pagado</button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {ocultas > 0 && (
              <button
                onClick={() => setVerTodos(true)}
                style={{ width: '100%', padding: '10px', background: '#f8f9fb', border: 0, borderTop: '1px solid #e2e8f0', color: '#1A3461', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              >
                ⌄ Ver {ocultas} {ocultas === 1 ? 'vencimiento más' : 'vencimientos más'}
              </button>
            )}
            {verTodos && lista.length > 3 && (
              <button
                onClick={() => setVerTodos(false)}
                style={{ width: '100%', padding: '10px', background: '#f8f9fb', border: 0, borderTop: '1px solid #e2e8f0', color: '#64748B', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                ⌃ Mostrar solo próximos 3
              </button>
            )}
          </div>
        )
      })()}

      {/* Suscribir Google Calendar */}
      {icsUrl && (
        <div style={{ marginTop: 16, padding: 14, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10 }}>
          <div style={{ fontWeight: 800, color: '#0c4a6e', fontSize: 13, marginBottom: 6 }}>
            📲 Suscribir en Google Calendar (una sola vez)
          </div>
          <ol style={{ margin: '0 0 10px 18px', padding: 0, fontSize: 12, color: '#334155', lineHeight: 1.6 }}>
            <li>Abrí <a href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl" target="_blank" rel="noopener" style={{ color: '#0284c7', fontWeight: 600 }}>calendar.google.com → Agregar calendario → Suscribirse desde URL</a></li>
            <li>Pegá la URL de abajo y dale "Agregar calendario".</li>
            <li>Los eventos van a aparecer con avisos 2 días antes y el mismo día a las 9:00. Cuando marcás pagado acá, desaparecen automáticamente en 1-3 horas.</li>
          </ol>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 10px' }}>
            <code style={{ flex: 1, fontSize: 11, color: '#475569', wordBreak: 'break-all', fontFamily: 'monospace' }}>{icsUrl}</code>
            <button onClick={copiarUrl} style={{ padding: '6px 12px', background: copiado ? '#16a34a' : '#0284c7', color: '#fff', border: 0, borderRadius: 5, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {copiado ? '✓ Copiado' : '📋 Copiar URL'}
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#64748B', marginTop: 8 }}>
            ⚠️ No compartas esta URL — incluye el token de seguridad. Si la URL se filtra, regenerá el token desde la BD.
          </div>
        </div>
      )}

      {/* Modal Marcar pagado */}
      {modal && (
        <ModalPago venc={modal} onClose={() => setModal(null)} onConfirm={marcarPagado} />
      )}
    </section>
  )
}

const th = { padding: '8px 10px', textAlign: 'left', fontSize: 11, color: '#64748B', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }
const td = { padding: '10px', verticalAlign: 'top' }
const btnSm = { padding: '5px 10px', background: '#1A3461', color: '#fff', border: 0, borderRadius: 5, fontSize: 12, fontWeight: 700, cursor: 'pointer' }

function ModalPago({ venc, onClose, onConfirm }) {
  const [form, setForm] = useState({
    fecha_pago: new Date().toISOString().slice(0, 10),
    monto_pagado: venc.monto_estimado || '',
    notas: '',
  })
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, maxWidth: 420, width: '100%', padding: 20 }}>
        <h3 style={{ margin: 0, color: '#1A3461', fontSize: 16 }}>Marcar como pagado</h3>
        <div style={{ fontSize: 13, color: '#64748B', marginTop: 4, marginBottom: 14 }}>
          {tipoLabel(venc.tipo, venc.periodo)} · Vencía {fmtFecha(venc.fecha_vencimiento)}
        </div>

        <label style={lbl}>Fecha de pago</label>
        <input type="date" value={form.fecha_pago} onChange={e => setForm({ ...form, fecha_pago: e.target.value })} style={inp} />

        <label style={lbl}>Monto pagado (opcional)</label>
        <input type="number" step="0.01" value={form.monto_pagado} onChange={e => setForm({ ...form, monto_pagado: e.target.value })} placeholder="$" style={inp} />

        <label style={lbl}>Notas (opcional)</label>
        <textarea value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} rows={2} placeholder="Ej: pagado con transferencia Itaú #12345" style={{ ...inp, fontFamily: 'inherit', resize: 'vertical' }} />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button onClick={onClose} style={{ padding: '8px 14px', background: '#f1f5f9', color: '#475569', border: 0, borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancelar</button>
          <button onClick={() => onConfirm(form)} style={{ padding: '8px 16px', background: '#16a34a', color: '#fff', border: 0, borderRadius: 6, fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>✓ Confirmar pago</button>
        </div>
      </div>
    </div>
  )
}

const lbl = { display: 'block', fontSize: 11, color: '#64748B', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 10, marginBottom: 4 }
const inp = { width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, color: '#1A3461', boxSizing: 'border-box' }
